import { useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { Typography } from '@gto/ui/typography'

import type {
  AffiliateAdminPartnerDetailSchema,
  AffiliatePaymentMethodRequestSchema,
} from '@/api/gen'
import { MutationError } from '@/components/mutation-error'
import { PayoutMethodFields } from '@/components/payout-method-fields'
import { PayoutMethodSummary } from '@/components/payout-method-summary'
import { SectionCard } from '@/components/section-card'
import { usePayoutMethodForm } from '@/components/use-payout-method-form'
import { isPayoutMethod } from '@/domain/payout-methods'

import { SectionHeader } from '../section-header'
import { usePartnerUpdate } from '../use-partner-update'

type PayoutInfoCardProps = {
  className?: string
  partner: Pick<
    AffiliateAdminPartnerDetailSchema,
    'payout_method' | 'payout_details' | 'has_payout_details'
  >
  partnerId: string
}

export const PayoutInfoCard = ({ className, partner, partnerId }: PayoutInfoCardProps) => {
  const [editing, setEditing] = useState(false)
  const { form, reset, handleSubmit, applyServerErrors } = usePayoutMethodForm()
  const { mutation, hasError, detail } = usePartnerUpdate(
    partnerId,
    () => setEditing(false),
    applyServerErrors,
  )

  const savedMethod = isPayoutMethod(partner.payout_method) ? partner.payout_method : null
  const hasSaved = savedMethod != null && partner.has_payout_details

  const startEditing = () => {
    // Details always start blank — the legacy free-form dicts don't map onto the
    // new structured shape, so a change is a full re-entry (server-validated).
    reset(savedMethod)
    setEditing(true)
    mutation.reset()
  }

  const cancelEditing = () => {
    setEditing(false)
    mutation.reset()
  }

  const submitPayoutMethod = (body: AffiliatePaymentMethodRequestSchema) => {
    mutation.mutate({ partnerId, data: { payout_method_update: body } })
  }

  return (
    <SectionCard
      className={className}
      onSubmit={editing ? handleSubmit(submitPayoutMethod) : undefined}
    >
      <SectionHeader
        editing={editing}
        saving={mutation.isPending}
        title={<FormattedMessage id="settings.payoutMethod" />}
        onCancel={cancelEditing}
        onEdit={startEditing}
        onSave={() => undefined}
      />
      {editing ? (
        <div className="flex flex-col gap-3">
          <PayoutMethodFields
            disabled={mutation.isPending}
            form={form}
          />
          <MutationError
            detail={detail}
            messageId="admin.partnerDetail.save.error"
            show={hasError}
          />
        </div>
      ) : hasSaved ? (
        <PayoutMethodSummary
          details={partner.payout_details}
          method={partner.payout_method ?? ''}
        />
      ) : (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          <FormattedMessage id="admin.partnerDetail.payout.none" />
        </Typography>
      )}
    </SectionCard>
  )
}
